import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from '../common';
import { Heading, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import SessionDetailsModal from '../sessions/SessionDetailsModal';
import ClubLogo from '../clubs/ClubLogo';
import { SessionTypeIcon, sessionTypeTone } from '../clubs/dashboard/SessionTypeChip';
import { getSessionTypeShortLabel } from '../../utils/sessionTypeUtils';
import {
  signupForSession,
  cancelSignup,
  getPlayerSignupStatus,
  isPlayerOnLiveRoster,
} from '../../services/sessionManagementService';
import { formatSessionTimeRange } from '../../utils/homeSessionUtils';
import { isHostSelectSignup } from '../../utils/signupSelection';
import { homeSectionHeading, homeElevatedInset } from './homeDashboardStyles';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';

const homeSessionsStripClass = `flex items-stretch gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x`;
const homeSessionsCardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start';

function normalizeSession(session) {
  const confirmed = session.confirmed_players ?? session.confirmedPlayers ?? 0;
  const waitlist = session.waitlist_players ?? session.waitlistPlayers ?? 0;
  return {
    ...session,
    current_players: session.current_players ?? confirmed,
    confirmed_players: confirmed,
    waitlist_players: waitlist,
    max_players: session.max_players || 16,
  };
}

function canEnterLiveSession(session, userSignupStatus) {
  if (session?.session_status !== 'ongoing') return false;
  return Boolean(
    session.isInSessionRoster
    || userSignupStatus?.isInSessionRoster
    || session.permissions?.canManage
  );
}

function getSignupStatusLabel(session, userSignupStatus) {
  if (canEnterLiveSession(session, userSignupStatus)) return null;
  const signupStatus = userSignupStatus?.signup_status;
  if (signupStatus === 'confirmed') {
    return isHostSelectSignup(session) ? 'Confirmed' : 'Signed up';
  }
  if (signupStatus === 'waitlist') {
    return `Waitlist #${userSignupStatus.waitlist_position}`;
  }
  if (session?.session_status === 'ongoing') return null;
  return 'Sign up now';
}

function HomeSessionCard({ session, userSignupStatus, onOpen, isNext = false }) {
  const isOngoing = session.session_status === 'ongoing';
  const sessionTitle = session.name || 'Session';
  const clubName = session.clubName || session.clubs?.name;
  const clubLogoUrl = session.clubLogoImageUrl ?? session.clubs?.logo_image_url ?? null;
  const datePart = String(session.session_date || '').split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const sessionDate = year && month && day
    ? format(new Date(year, month - 1, day), 'EEE, MMM d')
    : 'TBD';
  const sessionTime = formatSessionTimeRange(session.start_time, session.end_time, { compact: true });
  const confirmed = isOngoing
    ? (session.players_in_session ?? session.confirmed_players ?? 0)
    : (session.confirmed_players ?? 0);
  const maxPlayers = session.max_players || 16;
  const fillPercent = maxPlayers > 0 ? Math.min(100, Math.round((confirmed / maxPlayers) * 100)) : 0;
  const canEnterLive = canEnterLiveSession(session, userSignupStatus);
  const statusLabel = getSignupStatusLabel(session, userSignupStatus);
  const eyebrow = isOngoing ? 'Live now' : isNext ? 'Your next session' : 'Upcoming';
  const typeTone = sessionTypeTone(session);
  const typeLabel = getSessionTypeShortLabel(session);
  const openLabel = canEnterLive
    ? `Enter live session ${sessionTitle}`
    : `View details for ${sessionTitle}`;

  return (
    <div role="listitem" className={`flex flex-col ${homeSessionsCardClass}`}>
      <Typography
        variant="caption"
        className={`mb-2 block h-5 truncate px-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ${
          canEnterLive ? 'text-success-dark' : 'text-text-muted'
        }`}
      >
        {eyebrow}
      </Typography>
      <Card
        variant="default"
        className={`relative flex flex-1 flex-row items-center gap-3 ${homeElevatedInset} ${
          canEnterLive ? '!border-2 !border-success ring-success/20' : ''
        }`}
      >
        <button
          type="button"
          onClick={() => onOpen(session)}
          aria-label={openLabel}
          className="absolute inset-0 z-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        />
        <div className="pointer-events-none relative z-10 flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Typography className="line-clamp-2 text-sm font-semibold leading-snug text-text-primary">
                {sessionTitle}
              </Typography>
              {clubName ? (
                <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
                  <ClubLogo
                    logoImageUrl={clubLogoUrl}
                    clubName={clubName}
                    size="sm"
                    className="shrink-0"
                  />
                  <Typography variant="caption" className="truncate text-text-secondary">
                    {clubName}
                  </Typography>
                </div>
              ) : null}
            </div>
            {canEnterLive ? (
              <Typography
                variant="caption"
                className="shrink-0 pt-0.5 font-semibold text-success-dark"
              >
                Enter session
              </Typography>
            ) : statusLabel ? (
              <Typography
                variant="caption"
                className={`shrink-0 pt-0.5 ${
                  statusLabel === 'Sign up now'
                    ? 'font-semibold text-primary'
                    : 'font-medium text-text-secondary'
                }`}
              >
                {statusLabel}
              </Typography>
            ) : null}
          </div>

          <Typography variant="caption" className="text-text-secondary">
            {sessionDate} · {sessionTime}
          </Typography>

          <div className="mt-auto flex items-center gap-2">
            <span className="shrink-0 text-xs font-semibold tabular-nums text-text-primary">
              {confirmed}/{maxPlayers}
            </span>
            <div
              className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-border-light"
              role="progressbar"
              aria-label="Confirmed spots filled"
              aria-valuemin={0}
              aria-valuemax={maxPlayers}
              aria-valuenow={confirmed}
            >
              <div
                className={`h-full rounded-full ${confirmed >= maxPlayers ? 'bg-warning' : 'bg-primary'}`}
                style={{ width: `${fillPercent}%` }}
              />
            </div>
            <span
              className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${typeTone.well}`}
              aria-label={typeLabel}
            >
              <SessionTypeIcon session={session} className="h-4 w-4" />
            </span>
          </div>
        </div>
        <svg
          className="pointer-events-none relative z-10 h-5 w-5 shrink-0 text-text-muted"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Card>
    </div>
  );
}

/**
 * Home sessions strip: next session first, then other club sessions in the next two weeks.
 */
export function HomeSessionsCarousel({
  nextSession,
  upcomingSessions,
  currentUser,
  memberClubIds,
  onSessionsChanged,
}) {
  const navigate = useNavigate();
  const [userSignupStatuses, setUserSignupStatuses] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const nextGame = useMemo(
    () => (nextSession ? normalizeSession(nextSession) : null),
    [nextSession]
  );
  const restSessions = useMemo(() => {
    const seen = new Set(nextGame?.id ? [nextGame.id] : []);
    const preferClubSessions = memberClubIds && memberClubIds.size > 0;
    const mapped = (upcomingSessions || [])
      .map(normalizeSession)
      .filter((session) => {
        if (seen.has(session.id)) return false;
        if (preferClubSessions && !memberClubIds.has(session.club_id)) return false;
        seen.add(session.id);
        return true;
      });
    return mapped;
  }, [upcomingSessions, nextGame?.id, memberClubIds]);

  const allCards = useMemo(
    () => [nextGame, ...restSessions].filter(Boolean),
    [nextGame, restSessions]
  );

  const refreshSignupStatuses = useCallback(async () => {
    if (!currentUser?.id || !allCards.length) {
      setUserSignupStatuses({});
      return;
    }
    try {
      const statuses = {};
      await Promise.all(
        allCards.map(async (session) => {
          const signup = await getPlayerSignupStatus(session.id, currentUser.id);
          const rostered = session.session_status === 'ongoing'
            ? (session.isInSessionRoster || await isPlayerOnLiveRoster(session.id, currentUser.id))
            : Boolean(session.isInSessionRoster);
          statuses[session.id] = { ...signup, isInSessionRoster: rostered };
        })
      );
      setUserSignupStatuses(statuses);
    } catch (err) {
      console.error('Error fetching signup statuses for Sessions carousel:', err);
    }
  }, [currentUser?.id, allCards]);

  useEffect(() => {
    refreshSignupStatuses();
  }, [refreshSignupStatuses]);

  const handleOpen = (session) => {
    if (canEnterLiveSession(session, userSignupStatuses[session.id])) {
      navigate(`/club/${session.club_id}/session/${session.id}`);
      return;
    }
    setSelectedSessionId(session.id);
    setShowDetailsModal(true);
  };

  const handleSignup = async (sessionId) => {
    if (!currentUser?.id) return;
    try {
      await signupForSession(sessionId, currentUser.id);
      await refreshSignupStatuses();
      onSessionsChanged?.();
    } catch (err) {
      console.error('Error signing up from Home sessions:', err);
    }
  };

  const handleCancelSignup = async (sessionId) => {
    if (!currentUser?.id) return;
    try {
      await cancelSignup(sessionId, currentUser.id);
      await refreshSignupStatuses();
      onSessionsChanged?.();
    } catch (err) {
      console.error('Error cancelling signup from Home sessions:', err);
    }
  };

  if (!allCards.length) return null;

  return (
    <section>
      <div className="mb-5 flex items-center justify-between gap-3">
        <Heading level={2} className={homeSectionHeading}>
          Sessions
        </Heading>
        <Button
          onClick={() => navigate('/upcoming-sessions')}
          variant="text"
          className="shrink-0 text-sm font-semibold text-primary"
        >
          View all
        </Button>
      </div>

      <div className={homeSessionsStripClass} role="list">
        {nextGame ? (
          <HomeSessionCard
            session={nextGame}
            userSignupStatus={userSignupStatuses[nextGame.id]}
            onOpen={handleOpen}
            isNext
          />
        ) : null}
        {nextGame && restSessions.length > 0 ? (
          <div className="flex w-6 shrink-0 flex-col self-stretch" aria-hidden>
            <div className="mb-2 h-5" />
            <div className="flex flex-1 items-center justify-center">
              <div className="h-[70%] w-px bg-border-medium" />
            </div>
          </div>
        ) : null}
        {restSessions.map((session) => (
          <HomeSessionCard
            key={session.id}
            session={session}
            userSignupStatus={userSignupStatuses[session.id]}
            onOpen={handleOpen}
          />
        ))}
      </div>

      {allCards.length > 1 ? (
        <Typography variant="caption" className="mt-3 px-2 text-center text-text-muted max-sm:block sm:hidden">
          Swipe sideways for more sessions · or tap <span className="font-semibold text-text-secondary">View all</span>
        </Typography>
      ) : null}

      {showDetailsModal && selectedSessionId ? (
        <SessionDetailsModal
          sessionId={selectedSessionId}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedSessionId(null);
            void refreshSignupStatuses();
          }}
          onSignup={handleSignup}
          onCancelSignup={handleCancelSignup}
          currentUser={currentUser}
          userSignupStatus={userSignupStatuses[selectedSessionId]}
        />
      ) : null}
    </section>
  );
}
